import React, { useMemo } from 'react';
import { Loader2, Pencil } from 'lucide-react';
import { Button } from '../../../Button';
import { validateUsername } from '../../../../../utils/accountValidation';

interface UsernameEditorProps {
  username: string | undefined;
  isEditing: boolean;
  newUsername: string;
  usernameLoading: boolean;
  checkingUsername: boolean;
  usernameAvailable: boolean | null;
  onStartEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onUsernameChange: (value: string) => void;
}

export const UsernameEditor: React.FC<UsernameEditorProps> = ({
  username,
  isEditing,
  newUsername,
  usernameLoading,
  checkingUsername,
  usernameAvailable,
  onStartEdit,
  onSave,
  onCancel,
  onUsernameChange,
}) => {
  const validation = useMemo(() => validateUsername(newUsername), [newUsername]);

  if (isEditing) {
    return (
      <div className="space-y-2">
        <input
          type="text"
          value={newUsername}
          onChange={e => onUsernameChange(e.target.value)}
          maxLength={20}
          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
        {/* Validation / availability indicator */}
        {newUsername && (
          <div className="text-xs mt-1">
            {!validation.isValid ? (
              <span className="text-red-600">{validation.error}</span>
            ) : checkingUsername ? (
              <span className="text-muted-foreground">Checking availability...</span>
            ) : usernameAvailable === false ? (
              <span className="text-red-600">Username already taken</span>
            ) : usernameAvailable === true ? (
              <span className="text-green-600">Available</span>
            ) : null}
          </div>
        )}
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={onSave}
            disabled={
              usernameLoading ||
              !validation.isValid ||
              usernameAvailable === false
            }
          >
            {usernameLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between w-full">
      <span className="text-sm font-semibold text-foreground">Username:</span>
      <div className="flex items-center gap-1">
        <span className="text-sm text-muted-foreground">
          {username || 'No username set'}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onStartEdit}
          className="h-auto w-auto p-0 hover:bg-transparent"
          aria-label="Edit username"
          title="Edit"
        >
          <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </div>
    </div>
  );
};
